/**
 * Focus Management Hook
 * Provides utilities for managing focus in forms and interactive components
 * Enhances accessibility and keyboard navigation experience
 */

import { useEffect, useRef, useCallback } from 'react';

export interface FocusManagementOptions {
  /**
   * Whether to automatically focus the first field on mount
   */
  autoFocusFirst?: boolean;

  /**
   * Whether to trap focus within the container
   */
  trapFocus?: boolean;

  /**
   * Whether to restore focus when component unmounts
   */
  restoreFocus?: boolean;
}

/**
 * Hook for managing focus in forms and interactive components
 */
export function useFocusManagement(options: FocusManagementOptions = {}) {
  const {
    autoFocusFirst = false,
    trapFocus = false,
    restoreFocus = false,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  // Store the previously focused element when component mounts
  useEffect(() => {
    if (restoreFocus) {
      previousActiveElement.current = document.activeElement;
    }

    return () => {
      // Restore focus when component unmounts
      if (restoreFocus && previousActiveElement.current) {
        (previousActiveElement.current as HTMLElement).focus?.();
      }
    };
  }, [restoreFocus]);

  // Auto-focus first focusable element
  useEffect(() => {
    if (autoFocusFirst && containerRef.current) {
      const firstFocusable = getFirstFocusableElement(containerRef.current);
      if (firstFocusable) {
        // Small delay to ensure DOM is ready
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }
  }, [autoFocusFirst]);

  // Focus trap implementation
  useEffect(() => {
    if (!trapFocus || !containerRef.current) return;

    const container = containerRef.current;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [trapFocus]);

  // Focus management utilities
  const focusFirst = useCallback(() => {
    if (!containerRef.current) return;
    const firstFocusable = getFirstFocusableElement(containerRef.current);
    firstFocusable?.focus();
  }, []);

  const focusLast = useCallback(() => {
    if (!containerRef.current) return;
    const focusableElements = getFocusableElements(containerRef.current);
    const lastFocusable = focusableElements[focusableElements.length - 1];
    lastFocusable?.focus();
  }, []);

  const focusNext = useCallback(() => {
    if (!containerRef.current) return;
    const focusableElements = getFocusableElements(containerRef.current);
    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLElement
    );
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex]?.focus();
  }, []);

  const focusPrevious = useCallback(() => {
    if (!containerRef.current) return;
    const focusableElements = getFocusableElements(containerRef.current);
    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLElement
    );
    const previousIndex =
      currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
    focusableElements[previousIndex]?.focus();
  }, []);

  return {
    containerRef,
    focusFirst,
    focusLast,
    focusNext,
    focusPrevious,
  };
}

/**
 * Get all focusable elements within a container
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  return Array.from(container.querySelectorAll(focusableSelectors)).filter(
    element => {
      // Ensure element is visible and not hidden
      const computedStyle = window.getComputedStyle(element as HTMLElement);
      return (
        computedStyle.display !== 'none' &&
        computedStyle.visibility !== 'hidden' &&
        (element as HTMLElement).offsetWidth > 0 &&
        (element as HTMLElement).offsetHeight > 0
      );
    }
  ) as HTMLElement[];
}

/**
 * Get the first focusable element within a container
 */
function getFirstFocusableElement(container: HTMLElement): HTMLElement | null {
  const focusableElements = getFocusableElements(container);
  return focusableElements[0] || null;
}

/**
 * Hook for managing focus on error conditions
 */
export function useErrorFocus() {
  const focusErrorElement = useCallback((errorElementId?: string) => {
    if (!errorElementId) return;

    const errorElement = document.getElementById(errorElementId);
    if (errorElement) {
      // Focus the element and announce to screen readers
      errorElement.focus();

      // If it's an input with an error, focus the input instead
      const associatedInput = document.querySelector(
        `[aria-describedby*="${errorElementId}"]`
      );
      if (associatedInput && associatedInput instanceof HTMLElement) {
        associatedInput.focus();
      }
    }
  }, []);

  const focusFirstError = useCallback(() => {
    const firstErrorElement = document.querySelector(
      '[role="alert"], [aria-invalid="true"]'
    );
    if (firstErrorElement && firstErrorElement instanceof HTMLElement) {
      // If it's an error message, focus the associated input
      if (firstErrorElement.getAttribute('role') === 'alert') {
        const inputId = firstErrorElement.id?.replace('-error', '');
        const associatedInput = inputId
          ? document.getElementById(inputId)
          : null;
        if (associatedInput) {
          associatedInput.focus();
          return;
        }
      }

      // Otherwise focus the error element itself
      firstErrorElement.focus();
    }
  }, []);

  return {
    focusErrorElement,
    focusFirstError,
  };
}
